/**
 * Reparto de un importe en centavos entre participantes (D4).
 *
 * Algoritmo: **mayor resto**, con desempate determinístico por `sortOrder` y,
 * si aún hay empate, por `participantId`.
 *
 * Garantía: la suma de las partes es EXACTAMENTE igual al total. De esta
 * propiedad depende el invariante I6 (`Σ saldos = 0`).
 */
import { assertCents, roundHalfAwayFromZero, type Cents } from '@/core/money/cents';
import { EconomicsError, type SplitLine } from './types';

/** Escala de los porcentajes: 4 decimales, como `numeric(7,4)`. */
const PERCENT_SCALE = 10_000n;
const FULL = 100n * PERCENT_SCALE; // 1.000.000 = 100 %

export function assertSplitsAreComplete(lines: readonly SplitLine[], label = 'reparto'): void {
  if (lines.length === 0) {
    throw new EconomicsError(`El ${label} no tiene líneas`);
  }

  const seen = new Set<string>();
  let scaledTotal = 0n;

  for (const line of lines) {
    if (seen.has(line.participantId)) {
      throw new EconomicsError(`El ${label} repite al participante ${line.participantId}`);
    }
    seen.add(line.participantId);

    if (!(line.percentage > 0) || line.percentage > 100) {
      throw new EconomicsError(
        `Porcentaje fuera de rango en el ${label}: ${line.percentage} (${line.participantId})`,
      );
    }
    scaledTotal += BigInt(roundHalfAwayFromZero(line.percentage * Number(PERCENT_SCALE)));
  }

  if (scaledTotal !== FULL) {
    const pct = Number(scaledTotal) / Number(PERCENT_SCALE);
    throw new EconomicsError(`El ${label} suma ${pct} % y debe sumar exactamente 100 %`);
  }
}

/**
 * Reparte `totalCents` según `lines`.
 * Soporta totales negativos: el signo se aplica al final, de modo que la suma
 * sigue coincidiendo con el total.
 */
export function allocateCents(totalCents: Cents, lines: readonly SplitLine[]): Map<string, Cents> {
  assertCents(totalCents, 'total a repartir');
  assertSplitsAreComplete(lines);

  const result = new Map<string, Cents>();
  if (totalCents === 0) {
    for (const line of lines) result.set(line.participantId, 0);
    return result;
  }

  const sign = totalCents < 0 ? -1 : 1;
  const absTotal = BigInt(Math.abs(totalCents));

  const parts = lines.map((line) => {
    const scaled = BigInt(roundHalfAwayFromZero(line.percentage * Number(PERCENT_SCALE)));
    const numerator = absTotal * scaled;
    return {
      participantId: line.participantId,
      sortOrder: line.sortOrder,
      base: numerator / FULL,
      remainder: numerator % FULL,
    };
  });

  const distributed = parts.reduce((acc, p) => acc + p.base, 0n);
  let residue = absTotal - distributed;

  // Mayor resto primero; empate por sortOrder y luego por participantId.
  const order = [...parts].sort((a, b) => {
    if (a.remainder !== b.remainder) return a.remainder > b.remainder ? -1 : 1;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.participantId < b.participantId ? -1 : 1;
  });

  const bonus = new Map<string, bigint>();
  for (const p of order) {
    if (residue <= 0n) break;
    bonus.set(p.participantId, 1n);
    residue -= 1n;
  }

  for (const p of parts) {
    const amount = p.base + (bonus.get(p.participantId) ?? 0n);
    const cents = sign * Number(amount);
    // Normaliza -0 a 0: JavaScript distingue ambos y PostgreSQL no. La paridad
    // TS↔SQL exige un único cero (ver parity.db.test.ts).
    result.set(p.participantId, cents === 0 ? 0 : cents);
  }

  return result;
}

/**
 * Construye un reparto en partes iguales que suma EXACTAMENTE 100 %.
 *
 * Con 4 decimales, 100 no siempre es divisible en partes iguales: entre 3
 * participantes cada uno tendría 33,3333 % y la suma daría 99,9999 %. Este
 * helper reparte las milésimas de porciento sobrantes por mayor resto, con el
 * mismo desempate que `allocateCents`: gana el de menor `sortOrder`.
 *
 * Existe para que **el usuario no tenga que resolver el residuo a mano**: la UI
 * llama a esta función y obtiene porcentajes ya válidos.
 */
/**
 * Construye un reparto proporcional a un peso, que suma EXACTAMENTE 100 %.
 *
 * Nace de la movilidad cuando el trabajo se hizo en más de un vehículo. En una
 * IATF se va tres veces al mismo campo —poner los dispositivos, retirarlos,
 * inseminar— y no siempre en la misma camioneta: dos viajes de Felipe y uno
 * mío son 200 km y 100 km, no «66,67 % y 33,33 %».
 *
 * La persona carga kilómetros, que es lo que sabe. Los porcentajes —que es lo
 * que la base guarda y valida (invariante I2)— los calcula esto.
 *
 * El reparto del resto es el mismo mayor-resto que `buildEvenSplit`, con el
 * mismo desempate, para que dos caminos distintos no den números distintos.
 *
 * Los pesos en cero se descartan: alguien que no manejó no va en el reparto, y
 * además la base rechaza un porcentaje de cero.
 */
export function buildSplitByWeights(
  entries: readonly { participantId: string; weight: number }[],
): SplitLine[] {
  const conPeso = entries.filter((e) => e.weight > 0);

  if (conPeso.length === 0) {
    throw new EconomicsError('No se puede repartir sin ningún peso mayor que cero');
  }
  if (new Set(conPeso.map((e) => e.participantId)).size !== conPeso.length) {
    throw new EconomicsError('Hay participantes repetidos en el reparto');
  }
  if (conPeso.some((e) => !Number.isFinite(e.weight))) {
    throw new EconomicsError('Hay un peso que no es un número');
  }

  // A enteros antes de dividir: los kilómetros tienen dos decimales en la base
  // (`numeric(10,2)`) y hacer la cuenta en coma flotante trae restos que no son
  // restos, sino error de representación.
  const escalados = conPeso.map((e) => BigInt(roundHalfAwayFromZero(e.weight * 100)));
  const total = escalados.reduce((a, b) => a + b, 0n);
  if (total === 0n) {
    throw new EconomicsError('No se puede repartir sin ningún peso mayor que cero');
  }

  const partes = conPeso.map((e, i) => {
    const numerador = FULL * escalados[i]!;
    return {
      participantId: e.participantId,
      sortOrder: i + 1,
      base: numerador / total,
      resto: numerador % total,
    };
  });

  let residuo = FULL - partes.reduce((a, p) => a + p.base, 0n);

  const orden = [...partes].sort((a, b) => {
    if (a.resto !== b.resto) return a.resto > b.resto ? -1 : 1;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.participantId < b.participantId ? -1 : 1;
  });

  const extra = new Map<string, bigint>();
  for (const p of orden) {
    if (residuo <= 0n) break;
    extra.set(p.participantId, 1n);
    residuo -= 1n;
  }

  return partes.map((p) => ({
    participantId: p.participantId,
    percentage: Number(p.base + (extra.get(p.participantId) ?? 0n)) / Number(PERCENT_SCALE),
    sortOrder: p.sortOrder,
  }));
}

export function buildEvenSplit(participantIds: readonly string[]): SplitLine[] {
  if (participantIds.length === 0) {
    throw new EconomicsError('No se puede construir un reparto sin participantes');
  }
  if (new Set(participantIds).size !== participantIds.length) {
    throw new EconomicsError('Hay participantes repetidos en el reparto');
  }

  const n = BigInt(participantIds.length);
  const base = FULL / n;
  let residue = FULL - base * n;

  return participantIds.map((participantId, index) => {
    let scaled = base;
    if (residue > 0n) {
      scaled += 1n;
      residue -= 1n;
    }
    return {
      participantId,
      percentage: Number(scaled) / Number(PERCENT_SCALE),
      sortOrder: index + 1,
    };
  });
}
